import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { OrderAssignmentStatusFilter, Params } from 'constants/misc'
import { useEffect, useMemo, useState } from 'react'

import constate from 'constate'
import moment from 'moment'
import { useBkbnSearchParams } from 'utils/hooks/useBkbnSearchParams'
import { useDebouncedMemo } from 'utils/helpers'
import { useGetPropertyBrokers } from 'dataQueries'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

export enum FilterType {
  MANAGER = 'MANAGER',
  DATE_RANGE = 'DATE_RANGE',
  ORDER_STATUS = 'ORDER_STATUS',
}

const MANAGER_LIST_SIZE_LIMIT = 10

export const [PropertyDashboardFilterContextProvider, usePropertyDashboardFilter] = constate(() => {
  const { t } = useTranslation(['properties_dashboard'])

  const propertyParams = useBkbnSearchParams()
  const { userPropertyWorkspaceId } = useUserData()

  // MAIN URL PARAMS
  const searchProperty = useMemo(() => propertyParams.get(Params.SEARCH), [propertyParams])
  const brokerId = useMemo(() => propertyParams.get(Params.BROKER_ID), [propertyParams])
  const creationDateStart = useMemo(() => propertyParams.get(Params.PROPERTY_CREATION_DATE_START), [propertyParams])
  const creationDateEnd = useMemo(() => propertyParams.get(Params.PROPERTY_CREATION_DATE_END), [propertyParams])
  const orderStatus = useMemo(() => propertyParams.get(Params.PROPERTY_ORDER_STATUS), [propertyParams])

  const [selectedManager, setSelectedManager] = useState<string>('')
  const [searchManager, setSearchManager] = useState<string>('')
  const [disableSearchManager, setDisableSearchManager] = useState<boolean>(true)
  const [dateRangeStart, setDateRangeStart] = useState<moment.Moment | null>(null)
  const [dateRangeEnd, setDateRangeEnd] = useState<moment.Moment | null>(null)
  const [tempDateRangeStart, setTempDateRangeStart] = useState<moment.Moment | null>(null)
  const [tempDateRangeEnd, setTempDateRangeEnd] = useState<moment.Moment | null>(null)
  const [selectedOrderStatus, setSelectedOrderStatus] = useState<string>('')
  const [openFilter, setOpenFilter] = useState<FilterType | null>(null)
  const [isFilterIconActive, setIsFilterIconActive] = useState<boolean>(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false)
  const [showManagersInput, setShowManagersInput] = useState<boolean>(false)

  const managersParams = useDebouncedMemo(() => {
    return {
      size: MANAGER_LIST_SIZE_LIMIT,
      page: 0,
      query: searchManager ?? '',
    }
  }, [selectedManager], 300)

  const propertyManagersQuery = useGetPropertyBrokers(userPropertyWorkspaceId, managersParams)

  // Refetch property managers when workspace id changes
  useEffect(() => {
    if (!userPropertyWorkspaceId) return
    propertyManagersQuery.refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPropertyWorkspaceId])

  const managersList = useMemo(() => {
    if (propertyManagersQuery.isSuccess) {
      if (!showManagersInput && propertyManagersQuery.data?.data?.content?.length > 0) {
        setShowManagersInput(true)
      }

      return propertyManagersQuery.data?.data?.content
    }

    return []
    // eslint-disable-next-line react-hooks/exhaustive-deps -- showManagers
  }, [propertyManagersQuery.data?.data?.content, propertyManagersQuery.isSuccess])

  // Initialize date range from URL params
  useEffect(() => {
    if (creationDateStart) {
      setDateRangeStart(moment(creationDateStart))
    }
    if (creationDateEnd) {
      setDateRangeEnd(moment(creationDateEnd))
    }
  }, [creationDateStart, creationDateEnd])

  // Initialize order status from URL params
  useEffect(() => {
    if (orderStatus) {
      setSelectedOrderStatus(orderStatus)
    }
  }, [orderStatus])

  /** Handles the change of the selected manager by updating the state and setting the broker ID parameter.   */
  const handleChangeManager = (name: string, id?: string) => {
    setSelectedManager(name)

    // If the name is not the same as the selected manager, set the search manager to the name
    if (!id && name !== selectedManager) { setSearchManager(name) }

    // Sets the ID of the selected manager. If not provided, an empty string will be used.
    if (id || name === '') {
      propertyParams.set([Params.BROKER_ID, id ?? ''])
    }
  }

  /** Handles the removal of the selected manager by resetting the state and clearing the broker ID parameter.   */
  const handleRemoveManager = () => {
    setSelectedManager('')
    setSearchManager('')
    propertyParams.set([Params.BROKER_ID, ''])
  }

  /** Handles order status change */
  const handleOrderStatusChange = (status: string) => {
    setSelectedOrderStatus(status)
    propertyParams.set([Params.PROPERTY_ORDER_STATUS, status])
    logAnalyticsEvent(AnalyticsEvent.FILTER_PROPERTY, { filterType: FilterType.ORDER_STATUS })
  }

  /** Handles order status removal */
  const handleRemoveOrderStatus = () => {
    setSelectedOrderStatus('')
    propertyParams.set([Params.PROPERTY_ORDER_STATUS, ''])
  }

  /** Handles closing all filter dropdowns */
  const handleCloseFilter = () => {
    setOpenFilter(null)
  }

  /** Handles clear all filter */
  const handleClearAllFilters = () => {
    setSelectedManager('')
    setSelectedOrderStatus('')
    setTempDateRangeStart(null)
    setTempDateRangeEnd(null)
    setDateRangeStart(null)
    setDateRangeEnd(null)
    propertyParams.set(
      [Params.SEARCH, ''],
      [Params.BROKER_ID, ''],
      [Params.PROPERTY_ORDER_STATUS, ''],
      [Params.PROPERTY_CREATION_DATE_START, ''],
      [Params.PROPERTY_CREATION_DATE_END, '']
    )
  }

  /** Gets the display text for order status */
  const getOrderStatusDisplayText = (status: string): string => {
    switch (status) {
      case OrderAssignmentStatusFilter.ONGOING:
        return t('ongoing')
      case OrderAssignmentStatusFilter.NEEDS_ATTENTION:
        return t('needs_attention')
      case OrderAssignmentStatusFilter.DELIVERED:
        return t('delivered')
      case OrderAssignmentStatusFilter.CANCELLED:
        return t('cancelled')
      default:
        return ''
    }
  }

  // When managerList changes sets the selected manager based on the broker ID from the URL parameters.
  useEffect(() => {
    if (brokerId && !selectedManager) {
      const manager = managersList.find(manager => manager.id === brokerId)

      if (manager) setSelectedManager(manager.name)
    }

    // If the length of the managersList is less than 10, it disables the search manager input.
    if (managersList.length > 0 && managersList.length <= MANAGER_LIST_SIZE_LIMIT) {
      setDisableSearchManager(false)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managersList])

  return {
    showManagersInput,
    propertyParams,
    searchProperty,
    managersList,
    isDrawerOpen,
    setIsDrawerOpen,
    openFilter,
    setOpenFilter,
    handleCloseFilter,
    dateRangeStart,
    setDateRangeStart,
    dateRangeEnd,
    setDateRangeEnd,
    tempDateRangeStart,
    setTempDateRangeStart,
    tempDateRangeEnd,
    setTempDateRangeEnd,
    selectedOrderStatus,
    disableSearchManager,
    selectedManager,
    isFilterIconActive,
    setIsFilterIconActive,
    getOrderStatusDisplayText,
    handleOrderStatusChange,
    handleRemoveOrderStatus,
    handleChangeManager,
    handleRemoveManager,
    handleClearAllFilters
  }
})
